import React from 'react';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { FileText, Search, Eye, MessageSquare, Upload, Download, MapPin, Building2 } from 'lucide-react';

export default function ExpertDommagesTab({ ctx }) {
  const { dommages, setSelectedDommage, filterStatus, setFilterStatus, searchQuery, setSearchQuery, getStatutBadge, getTypeBadge, filteredDommages, activeTab } = ctx;
  return activeTab === 'dommages' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-slate-900">Mes Dommages</h1>
              <Badge className="bg-slate-700 text-white text-sm font-semibold">
                {filteredDommages.length} dommage(s)
              </Badge>
            </div>

            {/* Filtres et recherche */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Recherche */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Rechercher par numéro, entreprise, objet..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Filtre statut */}
                  <div className="flex gap-2">
                    <Button
                      variant={filterStatus === 'all' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus('all')}
                      className={filterStatus === 'all' ? 'bg-blue-600' : ''}
                    >
                      Tous
                    </Button>
                    <Button
                      variant={filterStatus === 'expertise_en_cours' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus('expertise_en_cours')}
                      className={filterStatus === 'expertise_en_cours' ? 'bg-purple-600' : ''}
                    >
                      En cours
                    </Button>
                    <Button
                      variant={filterStatus === 'traité' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilterStatus('traité')}
                      className={filterStatus === 'traité' ? 'bg-green-600' : ''}
                    >
                      Traités
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Liste des dommages */}
            <div className="space-y-4">
              {filteredDommages.length === 0 ? (
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center py-12">
                      <FileText className="mx-auto h-16 w-16 text-slate-300 mb-4" />
                      <p className="text-slate-600 text-lg">Aucun dommage trouvé</p>
                      <p className="text-slate-400 text-sm mt-2">
                        {searchQuery ? 'Essayez d\'ajuster vos critères de recherche' : 'Aucun dommage ne vous est assigné'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                filteredDommages.map(dommage => (
                  <Card key={dommage.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="pt-6">
                      <div className="flex flex-col lg:flex-row gap-6">
                        {/* Informations principales */}
                        <div className="flex-1 space-y-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <span className="font-mono text-lg font-bold text-slate-900">
                                  {dommage.numero_dommage}
                                </span>
                                {getStatutBadge(dommage.statut)}
                              </div>
                              <div className="flex items-center gap-2 mb-3">
                                {getTypeBadge(dommage.type_dommage)}
                              </div>
                            </div>
                          </div>

                          {/* Entreprise */}
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-600" />
                            <span className="font-semibold text-slate-900">{dommage.raison_sociale}</span>
                            {dommage.siret && (
                              <span className="text-sm text-slate-500">SIRET: {dommage.siret}</span>
                            )}
                          </div>

                          {/* Objet */}
                          <div className="bg-slate-50 p-3 rounded-lg">
                            <p className="text-sm font-semibold text-slate-700 mb-1">Objet du dommage :</p>
                            <p className="text-sm text-slate-900">{dommage.objet}</p>
                          </div>

                          {/* Dates et montant */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Date survenance</p>
                              <p className="font-medium text-slate-900">
                                {new Date(dommage.date_survenance).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            <div>
                              <p className="text-slate-500">Date déclaration</p>
                              <p className="font-medium text-slate-900">
                                {new Date(dommage.date_declaration).toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                            {dommage.montant_estime && (
                              <div>
                                <p className="text-slate-500">Montant estimé</p>
                                <p className="font-bold text-blue-600">
                                  {dommage.montant_estime.toLocaleString('fr-FR')} €
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Chantier */}
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-slate-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-slate-700">Chantier :</p>
                              <p className="text-slate-600">
                                {dommage.adresse_chantier}, {dommage.code_postal_chantier} {dommage.ville_chantier}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="lg:w-64 space-y-3">
                          <Button
                            onClick={() => setSelectedDommage(dommage)}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            Voir Détails
                          </Button>
                          
                          <Button variant="outline" className="w-full">
                            <MessageSquare className="mr-2 h-4 w-4" />
                            Commentaires ({dommage.commentaires?.length || 0})
                          </Button>

                          {dommage.rapport_expertise ? (
                            <Button variant="outline" className="w-full text-green-600 border-green-600">
                              <Download className="mr-2 h-4 w-4" />
                              Rapport
                            </Button>
                          ) : (
                            <Button variant="outline" className="w-full">
                              <Upload className="mr-2 h-4 w-4" />
                              Ajouter Rapport
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
  );
}
